import { IDTO, IResponseDto } from 'src/entities/common';
import { IPaymentReservation, IPaymentReservationDetails, IPaymentReservationRequest, IPaymentResultResponse, IPaymentUser } from 'src/entities/paymentReservation';
import { IPaymentSelectOption } from 'src/entities/pocket';
import { IReservationModel, isSignUpUser } from 'src/entities/reservation';
import { IReservationEndUser, ISignupUser, IUser, IUserBase } from 'src/entities/user';
import { setTimeOnDate } from '../dateTime';
import fetcher, { ContentType } from '../fetcher';
import { StepType } from '../getStepOrder';
import { handleError } from './errorHandler';
import { login, signUp } from './user';
import { AppLocale } from 'src/models/modules';

export interface IPostPaymentReservationParams {
    paymentUser: IPaymentUser;
    paymentReservations: IPaymentReservation[];
    user?: IUserBase | ISignupUser;
    shouldSignUp?: boolean;
    termsAccepted?: boolean;
    setUser?: (userData: IUser | null) => void;
}

export const postPaymentReservations = async (
    { 
        paymentUser, 
        paymentReservations,
        user, 
        shouldSignUp,
        termsAccepted, 
        setUser 
    }: IPostPaymentReservationParams): Promise<string> => {

    if (shouldSignUp && user && isSignUpUser(user)) {
        try {
            await signUp(user).then(async () => {
                if (setUser) {
                    await login({
                        username: user.Email,
                        password: user.Password,
                        keepLoggedIn: false
                    }).then(result => setUser(result));
                }
            });
        }
        catch (e) {
            return Promise.reject(e);
        }
    }

    const paymentReservationRequest: IPaymentReservationRequest = {
        Reservations: paymentReservations,
        User: paymentUser,
        TermsAccepted: termsAccepted ?? false
    }

    return fetcher.post<IDTO<string>>(`/PaymentReservations`, paymentReservationRequest, ContentType.Json)
        .then(response => {
            return response.Data;
        }, (errorDto: IResponseDto) => {
            return Promise.reject(handleError(errorDto));
        });
}

export const getPaymentReservations = async (clientKey?: string): Promise<IPaymentReservationDetails[] | null> => {
    if (clientKey) {
        return fetcher.get<IDTO<IPaymentReservationDetails[]>>(`/PaymentReservations/ByClientKey?clientKey=${clientKey}`)
            .then((restpJson) => restpJson.Data);
    }
    return null;
}

export const finalizeAfterPayment = async (paymentId: string | null, clientKey: string | null): Promise<void> => {
    return fetcher.post<void>(`/Finance/Barion?paymentId=${paymentId}&clientKey=${clientKey}`, {});
}

export const getPaymentResult = async (paymentId: string | null, clientKey: string | null, ignorePaymentStatus: boolean): Promise<IPaymentResultResponse> => {
    return fetcher.get<IDTO<IPaymentResultResponse>>
            (`/Finance/PaymentResult?paymentId=${paymentId}&clientKey=${clientKey}&ignoreStatus=${ignorePaymentStatus}`)
        .then((result) => result.Data);
}

export const reservationModelToPaymentRequest = (model: IReservationModel): IPaymentReservation => {
    const calendar = model[StepType.Calendar];
    const appointment = model[StepType.Date];
    const questions = model[StepType.CustomerData];
    const event = model[StepType.Timetable];
    const service = model[StepType.Service] || model[StepType.Timetable]!.service;

    const getArriveTime = (date: string) =>
        (questions && questions.arriveTime && setTimeOnDate(date, questions.arriveTime)) || date;

    return {
        CalendarId: calendar?.Id,
        ServiceId: service.Id,
        ScheduleId: event && event.schedule.Id,
        PersonCount: appointment ? appointment.personCount : event!.personCount,
        StartTime: appointment ? appointment.startTime : event!.schedule.Start,
        ArriveTime: getArriveTime(appointment ? appointment.startTime : event!.schedule.Start),
        ...(questions && { Questions: questions.answers })
    };
}

export const reservationEndUserToPaymentUser = (reservationEndUser: IReservationEndUser, selectedPayment: IPaymentSelectOption): IPaymentUser => {
    const { Name, Email, Phone } = reservationEndUser;

    return {
        Name,
        Email,
        Phone,
        PocketId: selectedPayment.Id,
        ...(selectedPayment.NeedsInvoice ? reservationEndUser.InvoiceDetails : {})
    };
}

export const redirectToPaymentLink = (paymentLink: string): boolean => {
    if (paymentLink) {
        window.location.href = paymentLink;
        return true;
    }

    return false;
}

export const getSimplePayPaymentInformationUrl = (locale: string): string => {
    return locale === AppLocale.Hu
        ? 'https://simplepartner.hu/PaymentService/Fizetesi_tajekoztato.pdf'
        : 'https://simplepartner.hu/PaymentService/Payment_information.pdf';
};